import { Shield, FileText, CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";

interface License {
  type: string;
  number: string;
  state: string;
}

interface InsuranceInfoProps {
  insurance: {
    insurance_coverage_policy?: string | null;
    license_numbers?: License[];
    accepted_insurance_types?: string[];
    offers_superbill?: boolean;
    additional_notes?: string | null;
  };
}

export default function PractitionerInsuranceInfo({ insurance }: InsuranceInfoProps) {
  const { t } = useTranslation('practitioners');

  if (!insurance) return null;

  const hasContent = insurance.insurance_coverage_policy || 
                    (insurance.license_numbers && insurance.license_numbers.length > 0) ||
                    (insurance.accepted_insurance_types && insurance.accepted_insurance_types.length > 0) ||
                    insurance.offers_superbill ||
                    insurance.additional_notes;

  if (!hasContent) return null;

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <CardTitle>{t('insuranceInfo.title')}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {insurance.license_numbers && insurance.license_numbers.length > 0 && (
          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {t('insuranceInfo.licenses')}
            </h4>
            <div className="space-y-2">
              {insurance.license_numbers.map((license, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <Badge variant="outline">
                    {license.type} - {license.number} ({license.state})
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {insurance.accepted_insurance_types && insurance.accepted_insurance_types.length > 0 && (
          <div>
            <h4 className="font-semibold mb-2">{t('insuranceInfo.acceptedInsurance')}</h4>
            <div className="flex flex-wrap gap-2">
              {insurance.accepted_insurance_types.map((type, index) => (
                <Badge key={index} variant="secondary">
                  {type}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {insurance.offers_superbill && (
          <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
            <CheckCircle className="h-4 w-4" />
            <span>{t('insuranceInfo.superbillAvailable')}</span>
          </div>
        )}

        {insurance.insurance_coverage_policy && (
          <div>
            <h4 className="font-semibold mb-2">{t('insuranceInfo.policy')}</h4>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {insurance.insurance_coverage_policy}
            </p>
          </div>
        )}

        {insurance.additional_notes && (
          <div>
            <h4 className="font-semibold mb-2">{t('insuranceInfo.additionalInfo')}</h4>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {insurance.additional_notes}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}